import { useTranslation } from 'react-i18next';
import { Text, Input, IconButton, Icon, IconType, Button, Divider } from '@/ui';
import VerticalDivider from '@/ui/VerticalDivider/VerticalDivider';
import ResidualsTable from './ResidualsTable';
import { Container, Header, Layout } from '@/components/SettingsLayout';
import SkeletonTable from '@/components/SkeletonLoader/SkeletonTable';
import styles from './styles.module.css';
import useFormatNumber from '@/hooks/useFormatNumber';
import { formatCurrency } from '@/utils/currency';
import {
  useGetPlatformBillingByReportQuery,
  useGetFeesReportHistoryQuery,
  useGetCurrentPlatformReportQuery,
  useGetResidualsReportByQueryQuery,
} from '@/store/slices/pricingSlice';
import MarkAsPaidModal from './components/MarkAsPaidModal';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useState, useMemo } from 'react';
import { debounce } from '@/utils/helpers';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetUser } from '@/hooks';
import { ROUTES } from '@/constants/constants';
import { FINAL_USERS } from '@/enums';
import { residualsDateFormatter } from '@/utils/helpers/dateFormater';
import { ResidualStatus } from '../pricing-forms/pricingHelpers';
import { EmptyResiduals } from './components/EmptyResiduals';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';
import accruingStatusIcon from '@/assets/icons/accruingStatus.svg';
import dueStatusIcon from '@/assets/icons/dueStatus.svg';
import paidStatusIcon from '@/assets/icons/paidStatus.svg';

const residualsRef = `${ROUTES.CDK}/${ROUTES.RESIDUALS}`;

function ResidualsView() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { company, currentUser } = useGetUser();

  const stateReport = (location.state as { report?: any })?.report;
  const { t } = useTranslation();
  const formatValue = useFormatNumber();
  const [searchQuery, setSearchQuery] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const isFinalUser = currentUser && FINAL_USERS.includes(currentUser.type);

  const { data: finalUserReports, refetch: refetchFinalUserReports } =
    useGetResidualsReportByQueryQuery(
      isFinalUser && company?._id
        ? {
            companyId: company._id,
            query: {
              searchValue: undefined,
              status: [ResidualStatus.DUE, ResidualStatus.ACCRUING, ResidualStatus.PAID],
              limit: 10,
              offset: 0,
            },
          }
        : skipToken,
    );

  const { data: reportHistory, isLoading: isHistoryLoading } = useGetFeesReportHistoryQuery(
    company?._id ? { companyId: company._id } : skipToken,
  );

  const { data: currentReport, isLoading: isCurrentReportLoading } =
    useGetCurrentPlatformReportQuery(company?._id ? { companyId: company._id } : skipToken);

  const finalUserArray = finalUserReports?.data ?? [];
  const historyArray = reportHistory?.data ?? [];

  const findReport = () => {
    if (!reportId) return null;

    // First check navigation state (always has the clicked report)
    if (stateReport && (stateReport.feesReportId || stateReport._id) === reportId) {
      return stateReport;
    }

    if (isFinalUser && finalUserArray.length) {
      const found = finalUserArray.find((r: any) => (r.feesReportId || r._id) === reportId);
      if (found) return found;
    }
    if (currentReport?._id === reportId) {
      return currentReport;
    }
    if (historyArray.length) {
      return historyArray.find((r: any) => (r.feesReportId || r._id) === reportId) || null;
    }

    return null;
  };
  const report = findReport();

  const { data: billingData, isLoading: isBillingLoading } = useGetPlatformBillingByReportQuery(
    reportId
      ? {
          feesReportId: reportId,
          query: {
            searchValue: searchQuery || undefined,
            limit: 1000,
            offset: 0,
          },
        }
      : skipToken,
  );

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value.toLowerCase());
    }, 500),
    [],
  );

  const residualsData = useMemo(() => {
    if (!Array.isArray(billingData)) return [];

    const filtered = searchQuery
      ? billingData.filter((item: any) => item.name?.toLowerCase().includes(searchQuery))
      : billingData;

    return filtered.map((item: any) => ({
      _id: item._id || item.id,
      companyName: item.name,
      transactionCount: item.transactionCount || 0,
      paymentVolume: item.totalPaymentAmount || item.totalPaymentVolume || 0,
      totalFee: item.finalPlatformFee || 0,
      finalFee: item.totalFinalFee || 0,
      residualEarned: item.totalResidualApplicationFee || 0,
    }));
  }, [billingData, searchQuery]);

  const reportCalculations = useMemo(() => {
    if (!report) return null;

    return {
      currentResidual: report.residualsEarned || 0,
      transactionCount: report.transactionCount || 0,
      totalFee: report.totalFeesCollected ? report.totalFeesCollected : 0,
      finalFee: report.totalFinalFee ? report.totalFinalFee : 0,
      paymentVolume: report.totalPaymentVolume,
      canMarkAsPaid: isFinalUser && report.status === ResidualStatus.DUE,
    };
  }, [report, isFinalUser]);

  const isLoading = isHistoryLoading || isCurrentReportLoading || isBillingLoading;
  const getStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return paidStatusIcon;
      case 'due':
        return dueStatusIcon;
      case 'accruing':
      default:
        return accruingStatusIcon;
    }
  };

  if (!reportId) {
    return (
      <Layout>
        <Container size='lg'>
          <Text size='s' weight='light-medium'>
            {t('Residuals.select_report') || 'Please select a report'}
          </Text>
        </Container>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <Container size='lg'>
          <Header>
            <Text size='ll' weight='semi-bold'>
              {t('Residuals.details')}
            </Text>
          </Header>
          <SkeletonTable />
        </Container>
      </Layout>
    );
  }

  if (!report || !reportCalculations) {
    return null;
  }

  const { currentResidual, transactionCount, totalFee, finalFee, paymentVolume, canMarkAsPaid } =
    reportCalculations;

  return (
    <Layout>
      <Container size='lg'>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconButton
              onClick={() => {
                const isCurrentReport = currentReport?._id === reportId;
                navigate(isCurrentReport ? residualsRef : `${residualsRef}?view=history`);
              }}
            >
              <Icon type={IconType.ArrowLeftLong} width={24} height={24} fill='#3D4C66' />
            </IconButton>
            <Text size='ll' weight='semi-bold'>
              {t('Residuals.details')}
            </Text>
          </div>
        </Header>
        <div className={styles.aggregateContainer}>
          <div className={styles.aggregateLeft}>
            {isFinalUser ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text size='m' weight='light-medium'>
                    {t('Residuals.reference_number') || 'Reference Number'}:
                  </Text>
                  <Text size='m' weight='light-medium'>
                    {report.feesReportId || report._id || '-'}
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text size='m' weight='light-medium'>
                    {t('Residuals.payment_period') || 'Pay Period'}:
                  </Text>
                  <Text size='m' weight='light-medium'>
                    {report.startDate && report.endDate
                      ? `${residualsDateFormatter(report.startDate)} - ${residualsDateFormatter(report.endDate)}`
                      : '-'}
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text size='m' weight='light-medium'>
                    {t('Residuals.pay_date') || 'Pay Date'}:
                  </Text>
                  <Text size='m' weight='light-medium'>
                    {report.datePaid ? residualsDateFormatter(report.datePaid) : '-'}
                  </Text>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text size='m' weight='light-medium'>
                    {t('Residuals.status') || 'Status'}:
                  </Text>
                  <ImageWithFallback
                    src={getStatusIcon(report.status)}
                    placeholderSrc={accruingStatusIcon}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Text size='m' weight='semi-bold'>
                    {t('Residuals.current_residual')}
                  </Text>
                  <Text size='s' weight='light-medium'>
                    {t('Residuals.current_residual_description')}
                  </Text>
                </div>
                <div className={styles.residualAmountSection}>
                  <Text size='xxl' weight='medium'>
                    {formatValue(currentResidual, false, 0)}/5000
                  </Text>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressBarFill}
                      style={{
                        width: `${Math.min((currentResidual / 5000) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <VerticalDivider height='100%' />
          <div className={styles.aggregateRight}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text size='m' weight='light-medium'>
                {t('Residuals.total_residual_earned')}
              </Text>
              <Text size='m' weight='light-medium'>
                {formatCurrency(currentResidual, 'USD')}
              </Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text size='m' weight='light-medium'>
                {t('Residuals.total_transaction_count')}
              </Text>
              <Text size='m' weight='light-medium'>
                {formatValue(transactionCount, false, 0)}
              </Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text size='m' weight='light-medium'>
                {t('Residuals.total_payment_volume')}:
              </Text>
              <Text size='m' weight='light-medium'>
                {formatCurrency(paymentVolume ? paymentVolume : 0, 'USD')}
              </Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text size='m' weight='light-medium'>
                {t('Residuals.total_fee_collected')}:
              </Text>
              <Text size='m' weight='light-medium'>
                {formatCurrency(totalFee, 'USD')}
              </Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text size='m' weight='light-medium'>
                {t('Residuals.total_final_fee')}:
              </Text>
              <Text size='m' weight='light-medium'>
                {formatCurrency(finalFee, 'USD')}
              </Text>
            </div>
          </div>
        </div>
        <Text size='m' weight='semi-bold'>
          {t('Residuals.breakdown')}
        </Text>
        <div className={styles.searchInput}>
          <Input
            size='small'
            variant='search'
            placeholder={t('Residuals.search_placeholder')}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        {residualsData.length > 0 ? (
          <ResidualsTable residuals={residualsData} />
        ) : (
          <EmptyResiduals />
        )}
        {canMarkAsPaid && (
          <>
            <div style={{ marginTop: '48px', marginBottom: '24px' }}>
              <Divider color='light' />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                marginBottom: '24px',
              }}
            >
              <Button variant='primary' onClick={() => setIsConfirmModalOpen(true)}>
                {t('Residuals.mark_as_paid')}
              </Button>
            </div>
          </>
        )}
      </Container>
      {reportId && (
        <MarkAsPaidModal
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          reportId={reportId}
          companyId={company?._id || ''}
          onSuccess={() => {
            if (isFinalUser) {
              refetchFinalUserReports();
            }
          }}
        />
      )}
    </Layout>
  );
}

export default ResidualsView;
